import { colors } from '../const';
import { hasCommand, runCommandSync } from './exec';
import { askQuestion } from './prompts';

export async function checkNodeVersion(): Promise<void> {
  const majorVersion = parseInt(process.version.slice(1).split('.')[0], 10);
  console.log(`${colors.dim}Step 1/4: Checking Node.js version (current: ${process.version})...${colors.reset}`);

  if (majorVersion < 18) {
    console.log(`⚠️  ${colors.yellow}Outdated Node.js version detected. v18.0.0 or higher is required.${colors.reset}`);
    console.log(`${colors.dim}Note: If you already have a higher version installed via nvm, please run 'nvm use <version>' first.${colors.reset}\n`);

    const answer = await askQuestion(`Would you like to install/upgrade Node.js v18+ automatically? (y/N): `);
    const installAutomatically = answer.toLowerCase() === 'y' || answer.toLowerCase() === 'yes';

    if (!installAutomatically) {
      console.log(`\n❌ ${colors.red}Aborted. Please manually upgrade Node.js to v18+ and try again.${colors.reset}\n`);
      process.exit(1);
    }
    
    const isWin = process.platform === 'win32';
    const isMac = process.platform === 'darwin';

    if (isWin) {
      if (hasCommand('winget')) {
        console.log(`\n📦 ${colors.green}winget package manager found! Installing Node.js v18+ in background...${colors.reset}`);
        const success = runCommandSync('winget', ['upgrade', 'OpenJS.NodeJS', '--silent', '--accept-package-agreements', '--accept-source-agreements']);
        if (success) {
          console.log(`\n✔ ${colors.green}Node.js installation triggered successfully. Please restart your terminal/command-prompt for changes to take effect.${colors.reset}\n`);
          process.exit(0);
        } else {
          console.log(`\n❌ ${colors.red}winget update failed. Please update Node.js manually at https://nodejs.org/${colors.reset}\n`);
          process.exit(1);
        }
      } else {
        console.log(`\n❌ ${colors.red}winget package manager is not installed on this system.${colors.reset}`);
        console.log(`${colors.bold}👉 To proceed, please either:${colors.reset}`);
        console.log(`   1. Install winget (App Installer) from the Microsoft Store.`);
        console.log(`   2. Or manually download and install Node.js (v18+) from: https://nodejs.org/\n`);
        process.exit(1);
      }
    } else if (isMac) {
      if (hasCommand('brew')) {
        console.log(`\n📦 ${colors.green}Homebrew package manager found! Installing Node.js v18+ in background...${colors.reset}`);
        const success = runCommandSync('brew', ['install', 'node']);
        if (success) {
          console.log(`\n✔ ${colors.green}Node.js installed successfully via Homebrew.${colors.reset}\n`);
        } else {
          console.log(`\n❌ ${colors.red}Homebrew installation failed. Please install Node.js manually at https://nodejs.org/${colors.reset}\n`);
          process.exit(1);
        }
      } else {
        console.log(`\n❌ ${colors.red}Homebrew package manager (brew) is not installed on this system.${colors.reset}`);
        console.log(`${colors.bold}👉 To proceed, please either:${colors.reset}`);
        console.log(`   1. Install Homebrew by running the following command in your terminal:`);
        console.log(`      ${colors.cyan}/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"${colors.reset}`);
        console.log(`   2. Or manually download and install Node.js (v18+) from: https://nodejs.org/\n`);
        process.exit(1);
      }
    } else {
      console.log(`\n❌ ${colors.red}Please install or update Node.js to v18+ using your system's package manager.${colors.reset}\n`);
      process.exit(1);
    }
  } else {
    console.log(`✔ ${colors.green}Node.js version (v${majorVersion}) is sufficient.${colors.reset}`);
  }
}
